/**
 * SubsidyDetail + SubsidyCard — Extracted from DetailPage.jsx (A12 Sprint 4).
 *
 * Fristen-Dashboard / Kalender / Programme tabs with application tracking.
 * SubsidyCard co-extracted as private helper (only used by SubsidyDetail).
 * Smallest-first extraction per Cross-Block Pattern 4.
 *
 * A6-26 fix: raw fetch (line 405) → apiClient.get
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, ApiError } from '../../lib/apiClient';
import EmptyState from '../../components/EmptyState';
import DeadlineDashboard from '../../components/DeadlineDashboard';
import PaymentCalendar from '../../components/PaymentCalendar';

// ─── SubsidyCard (private helper) ────────────────────────────────
function SubsidyCard({ p, daysLeft, catLabel, t, appStatus, appNotes, onStatusChange, onNotesChange }) {
  const [expanded, setExpanded] = useState(false);
  const [localNotes, setLocalNotes] = useState(appNotes || '');

  const STATUS_COLORS = {
    NOT_APPLIED: 'text-[var(--aif-text-muted)] bg-[var(--aif-bg-elevated)]',
    PREPARING: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30',
    SUBMITTED: 'text-blue-400 bg-blue-500/10 border-blue-500/30',
    APPROVED: 'text-green-400 bg-green-500/10 border-green-500/30',
    PAID: 'text-green-400 bg-green-500/10 border-green-500/30',
    REJECTED: 'text-red-400 bg-red-500/10 border-red-500/30',
  };

  return (
    <div className={`rounded-2xl border bg-[var(--aif-bg-surface)] overflow-hidden ${
      appStatus === 'PREPARING' ? 'border-yellow-500/30' :
      appStatus === 'SUBMITTED' ? 'border-blue-500/30' :
      appStatus === 'APPROVED' || appStatus === 'PAID' ? 'border-green-500/30' :
      appStatus === 'REJECTED' ? 'border-red-500/30' :
      'border-[var(--aif-border-default)]'
    }`}>
      <button onClick={() => setExpanded(!expanded)} className="w-full p-4 text-left">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h4 className="text-sm font-semibold text-[var(--aif-text-primary)] leading-tight">{p.name}</h4>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--aif-bg-elevated)] text-[var(--aif-text-muted)] border border-[var(--aif-border-default)]">
                {catLabel}
              </span>
              {p.source_authority && (
                <span className="text-[10px] text-[var(--aif-text-muted)]">{p.source_authority}</span>
              )}
            </div>
          </div>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 border ${STATUS_COLORS[appStatus] || STATUS_COLORS.NOT_APPLIED}`}>
            {t(`subsidy.${appStatus}`)}
          </span>
        </div>
        {p.eligibility_reason && (
          <p className="text-[11px] text-[var(--aif-text-muted)] mt-1.5 italic">{p.eligibility_reason}</p>
        )}
        <div className="flex items-center gap-3 mt-2.5 text-xs text-[var(--aif-text-secondary)]">
          {p.estimated_amount_eur > 0 && (
            <span className="font-semibold text-[var(--aif-text-primary)]">~{p.estimated_amount_eur.toLocaleString()} EUR</span>
          )}
          {p.deadline && (
            <span className={`flex items-center gap-1 ${
              daysLeft !== null && daysLeft < 0 ? 'text-[var(--aif-text-muted)]' :
              daysLeft !== null && daysLeft < 30 ? 'text-red-400 font-medium' :
              'text-[var(--aif-text-muted)]'
            }`}>
              {p.deadline}
              {daysLeft !== null && daysLeft < 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] bg-[var(--aif-bg-elevated)] text-[var(--aif-text-muted)] line-through">
                  {t('subsidy.deadlineExpired')}
                </span>
              )}
              {daysLeft !== null && daysLeft >= 0 && (
                <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] ${
                  daysLeft < 14 ? 'bg-red-500/20 text-red-400' :
                  daysLeft < 30 ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-[var(--aif-bg-elevated)] text-[var(--aif-text-muted)]'
                }`}>
                  {daysLeft === 0 ? t('subsidy.deadlineToday') : `${daysLeft}d`}
                </span>
              )}
            </span>
          )}
          <svg className={`w-3.5 h-3.5 text-[var(--aif-text-muted)] ml-auto transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-4 border-t border-[var(--aif-border-default)] pt-3 space-y-3">
          {/* Status dropdown */}
          <div>
            <label className="text-[10px] text-[var(--aif-text-muted)] uppercase font-medium">{t('subsidy.appStatus')}</label>
            <select
              value={appStatus}
              onChange={(e) => onStatusChange?.(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="w-full mt-1 px-3 py-2 rounded-xl text-xs bg-[var(--aif-bg-input)] border border-[var(--aif-border-default)] text-[var(--aif-text-primary)] focus:border-[var(--aif-color-primary)] outline-none"
            >
              {['NOT_APPLIED', 'PREPARING', 'SUBMITTED', 'APPROVED', 'PAID', 'REJECTED'].map(s => (
                <option key={s} value={s}>{t(`subsidy.${s}`)}</option>
              ))}
            </select>
          </div>
          {/* Notes */}
          <div>
            <label className="text-[10px] text-[var(--aif-text-muted)] uppercase font-medium">{t('subsidy.notes')}</label>
            <textarea
              value={localNotes}
              onChange={(e) => setLocalNotes(e.target.value)}
              onBlur={() => { if (localNotes !== appNotes) onNotesChange?.(localNotes); }}
              onClick={(e) => e.stopPropagation()}
              placeholder={t('subsidy.notesPlaceholder')}
              rows={2}
              className="w-full mt-1 px-3 py-2 rounded-xl text-xs bg-[var(--aif-bg-input)] border border-[var(--aif-border-default)] text-[var(--aif-text-primary)] focus:border-[var(--aif-color-primary)] outline-none resize-none"
            />
          </div>
          {p.description && <p className="text-xs text-[var(--aif-text-secondary)] leading-relaxed">{p.description}</p>}
          {p.link && (
            <a href={p.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[var(--aif-color-primary)] hover:underline">
              {t('subsidy.moreInfo')} ↗
            </a>
          )}
          {p.application_portal && (
            <a href={p.application_portal} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[var(--aif-color-primary)] hover:underline">
              {t('subsidy.applicationPortal')} ↗
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// ─── SubsidyDetail (main export) ─────────────────────────────────
export default function SubsidyDetail({ data, token }) {
  const { t } = useTranslation();
  const programs = data?.matched_programs || [];
  const [view, setView] = useState('fristen'); // 'fristen' | 'kalender' | 'programs'
  const [filter, setFilter] = useState('all');
  const [appData, setAppData] = useState({});

  // Load application tracking data (A6-26 fix: raw fetch → apiClient.get)
  useEffect(() => {
    if (!token) return;
    apiClient.get('/api/v1/aif/subsidies/applications')
      .then(d => { if (d?.applications) setAppData(d.applications); })
      .catch(() => {});
  }, [token]);

  const updateAppStatus = async (programId, updates) => {
    if (!token) return;
    try {
      const r = await apiClient.patch(`/api/v1/aif/subsidies/applications/${programId}`, updates, {
        entityType: 'subsidy_application', queueOnOffline: true,
      });
      if (!r.queued) setAppData(prev => ({ ...prev, [programId]: r.data }));
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
  };

  if (!programs.length) {
    return (
      <EmptyState
        icon="data"
        title={t('subsidies.emptyTitle')}
        subtitle={t('subsidies.emptySubtitle')}
        testId="subsidies-empty-state"
      />
    );
  }

  const totalAmount = programs.reduce((sum, p) => sum + (p.estimated_amount_eur || 0), 0);

  // Count statuses
  const statusCounts = { PREPARING: 0, SUBMITTED: 0, APPROVED: 0, PAID: 0 };
  Object.values(appData).forEach(a => {
    const st = a.status || 'NOT_APPLIED';
    if (statusCounts[st] !== undefined) statusCounts[st]++;
  });

  const FILTERS = [
    { key: 'all', label: t('detail.filterAll') },
    { key: 'preparing', label: t('subsidy.filterPreparing') },
    { key: 'submitted', label: t('subsidy.filterSubmitted') },
    { key: 'approved', label: t('subsidy.filterApproved') },
    { key: 'direct', label: t('detail.filterDirect') },
    { key: 'eco', label: t('detail.filterEco') },
  ];

  const filtered = filter === 'all' ? programs : programs.filter(p => {
    const pId = p.name?.replace(/\s+/g, '_').toLowerCase() || '';
    const appStatus = appData[pId]?.status || 'NOT_APPLIED';
    if (filter === 'preparing') return appStatus === 'PREPARING';
    if (filter === 'submitted') return appStatus === 'SUBMITTED';
    if (filter === 'approved') return appStatus === 'APPROVED' || appStatus === 'PAID';
    const name = (p.name || '').toLowerCase() + ' ' + (p.description || '').toLowerCase();
    if (filter === 'direct') return name.includes('direktzahl') || name.includes('basisprämie') || name.includes('einkommens');
    if (filter === 'eco') return name.includes('öko-regel') || name.includes('eco-scheme') || name.includes('öko-');
    return true;
  });

  return (
    <div className="space-y-4">
      {/* View toggle: Fristen | Kalender | Programme */}
      <div className="flex gap-1 p-1 rounded-xl bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)]">
        <button
          onClick={() => setView('fristen')}
          data-testid="tab-fristen"
          className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-all ${
            view === 'fristen' ? 'bg-[var(--aif-color-primary)] text-white' : 'text-[var(--aif-text-muted)]'
          }`}
        >
          📅 {t('deadline.tabTitle')}
        </button>
        <button
          onClick={() => setView('kalender')}
          data-testid="tab-kalender"
          className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-all ${
            view === 'kalender' ? 'bg-[var(--aif-color-primary)] text-white' : 'text-[var(--aif-text-muted)]'
          }`}
        >
          💶 {t('payment.tabTitle', 'Kalender')}
        </button>
        <button
          onClick={() => setView('programs')}
          data-testid="tab-programs"
          className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-all ${
            view === 'programs' ? 'bg-[var(--aif-color-primary)] text-white' : 'text-[var(--aif-text-muted)]'
          }`}
        >
          📋 {t('subsidy.tabTitle', 'Programme')}
        </button>
      </div>

      {/* Fristen-Dashboard view */}
      {view === 'fristen' && <DeadlineDashboard token={token} />}

      {/* Payment Calendar view */}
      {view === 'kalender' && (<PaymentCalendar token={token} />)}

      {/* Programme view */}
      {view === 'programs' && (
      <div className="space-y-4">
      {/* Summary */}
      <div className="text-center py-3">
        <div className="text-3xl font-bold text-[var(--aif-color-primary)]">
          ~{totalAmount.toLocaleString()} €
        </div>
        <div className="text-xs text-[var(--aif-text-muted)] mt-1">
          {programs.length} {t('dashboard.matchedPrograms')} | {t('subsidy.potential')}
        </div>
        {/* [DRAFT-PENDING-LEGAL-REVIEW] Disclaimer — see docs/legal/PENDING_REVIEW.md */}
        <div className="text-[10px] text-[var(--aif-text-muted)] mt-2 px-4 text-center leading-relaxed">
          ⚠️ {t('subsidy.disclaimer')}
        </div>
      </div>

      {/* Status summary bar */}
      {(statusCounts.PREPARING > 0 || statusCounts.SUBMITTED > 0 || statusCounts.APPROVED > 0) && (
        <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] text-xs">
          {statusCounts.PREPARING > 0 && <span className="text-yellow-400">{statusCounts.PREPARING} {t('subsidy.PREPARING')}</span>}
          {statusCounts.SUBMITTED > 0 && <span className="text-blue-400">{statusCounts.SUBMITTED} {t('subsidy.SUBMITTED')}</span>}
          {statusCounts.APPROVED > 0 && <span className="text-green-400">{statusCounts.APPROVED} {t('subsidy.APPROVED')}</span>}
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
        {FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
              filter === f.key
                ? 'bg-[var(--aif-color-primary)] text-white'
                : 'bg-[var(--aif-bg-surface)] text-[var(--aif-text-muted)] border border-[var(--aif-border-default)]'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Program cards */}
      {filtered.map((p, i) => {
        const daysLeft = p.deadline ? Math.ceil((new Date(p.deadline) - Date.now()) / 86400000) : null;
        const catLabels = {
          gap_basic: t('detail.filterDirect'), gap_redistributive: t('detail.filterDirect'),
          young_farmer: t('detail.filterDirect'), eco_scheme: t('detail.filterEco'),
          aecm: 'AECM', investment: t('detail.filterInvest'), loan: t('detail.filterInvest'),
        };
        const catLabel = catLabels[p.category] || p.category;
        const pId = p.name?.replace(/\s+/g, '_').toLowerCase() || `prog_${i}`;
        const appStatus = appData[pId]?.status || 'NOT_APPLIED';
        const appNotes = appData[pId]?.notes || '';
        return (
          <SubsidyCard
            key={i} p={p} daysLeft={daysLeft} catLabel={catLabel} t={t}
            appStatus={appStatus} appNotes={appNotes}
            onStatusChange={(status) => updateAppStatus(pId, { status })}
            onNotesChange={(notes) => updateAppStatus(pId, { notes })}
          />
        );
      })}
    </div>
    )}
  </div>
  );
}
